import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import classNames from "classnames";
import { useMemo } from "react";
import styles from "./style.module.scss";

interface TablePagerProps {
  /** Zero-based. */
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
  /** Overrides the generated "Showing 1-8 of 14" line. */
  summary?: string;
  className?: string;
}

/** Footer pager shared by every table in the module. */
function TablePager({
  page,
  pageSize,
  total,
  onChange,
  summary,
  className,
}: TablePagerProps) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pageCount - 1);
  const from = current * pageSize;

  const label = useMemo(() => {
    if (summary) return summary;
    if (!total) return "No records";
    return `Showing ${from + 1}–${Math.min(from + pageSize, total)} of ${total}`;
  }, [summary, total, from, pageSize]);

  /** Windowed page list: 1 … current-1 current current+1 … last. */
  const pages = useMemo<(number | "gap")[]>(() => {
    const all = Array.from({ length: pageCount }, (_, index) => index);
    if (pageCount <= 7) return all;

    const window = new Set<number>([
      // Always the first three and the last three.
      0,
      1,
      2,
      pageCount - 3,
      pageCount - 2,
      pageCount - 1,
      // Plus the current page and its neighbours.
      current - 1,
      current,
      current + 1,
    ]);

    const visible = all.filter(
      (index) => index >= 0 && index < pageCount && window.has(index)
    );

    const items: (number | "gap")[] = [];
    visible.forEach((index, position) => {
      if (position > 0 && index - visible[position - 1] > 1) items.push("gap");
      items.push(index);
    });
    return items;
  }, [pageCount, current]);

  return (
    <div className={classNames(styles.pager, className)}>
      <span className={styles.summary}>{label}</span>
      <div className={styles.controls}>
        <button
          type="button"
          aria-label="Previous page"
          className={styles.arrow}
          disabled={current === 0}
          onClick={() => onChange(Math.max(0, current - 1))}
        >
          <ChevronLeftIcon className={styles.arrowIcon} />
        </button>
        {pages.map((index, position) =>
          index === "gap" ? (
            <span key={`gap-${position}`} className={styles.gap} aria-hidden>
              …
            </span>
          ) : (
            <button
              key={index}
              type="button"
              aria-current={index === current ? "page" : undefined}
              className={classNames(
                styles.page,
                index === current && styles.pageActive
              )}
              onClick={() => onChange(index)}
            >
              {index + 1}
            </button>
          )
        )}
        <button
          type="button"
          aria-label="Next page"
          className={styles.arrow}
          disabled={current >= pageCount - 1}
          onClick={() => onChange(Math.min(pageCount - 1, current + 1))}
        >
          <ChevronRightIcon className={styles.arrowIcon} />
        </button>
      </div>
    </div>
  );
}

export default TablePager;
